import {
  CloseOutlined,
  PlusCircleOutlined,
  DeleteOutlined,
  SmileOutlined,
} from '@ant-design/icons';
import { BackButton, Button, Heading } from '@mm/components';
import { EmailRed } from '@mm/icons';
import { selectCampaignById } from '@mm/state/selectors/campaignSelectors';
import React, { useRef } from 'react';
import { useSelector } from 'react-redux';
import { SlideoutMenu as Menu } from '../../components/SlideoutMenu';
import { useSlideoutOnClose } from '../SlideoutEmail/SlideoutEmail';
import css from './AutomationSoLDEmailNotificationSettings.module.css';
import { Editor } from '../SlideoutEmail/editor/components/Editor';
import { StoreState } from '@mm/state/store-interfaces';
import { colors } from '@mm/utils';
import { randomId } from '../../../../../common/helpers/utils';
import { ReactComponent as BracketsOutlined } from '../SlideoutEmail/svg/brackets.svg';
import { SlideOutMenuType } from '@mm/state/interfaces/view';
import { EmailInput } from '../../../../../common/modals/InviteUserModal/EmailInput/EmailInput';
import AttributeContainingTextInput from '../../../AttributeContainingTextInput/AttributeContainingTextInput';
import { EmojiPicker } from '../../../../../common/components/EmojiPicker/EmojiPicker';
import AttributePicker from '../../../../../common/components/AttributePicker/AttributePicker';
import { getUpdatedTextWithAttribute } from '../../../../../common/components/AttributePicker/attribute-picker-helpers';
import { getUpdatedTextWithEmoji } from '../../../../../common/components/EmojiPicker/emoji-picker-helpers';
import { schema } from '../AutomationSoLDSMSNotificationSettings/AutomationSoLDSMSNotificationSettings';
import { useSoldEmailNotifications } from './use-sold-email-notifications';

export type IAutomationSoLDEmailNotificationSettings = {
  menuType: SlideOutMenuType;
  className?: string;
  campaignId?: number;
};

export const AutomationSoLDEmailNotificationSettings: React.FC<
  IAutomationSoLDEmailNotificationSettings
> = props => {
  const attributes = useSelector(
    (state: StoreState) => state.attribute.entities,
  );
  const { menuType, campaignId = -1 } = props;
  const campaign = useSelector(state => selectCampaignById(state, campaignId));

  const emailSubjectRef = useRef<HTMLTextAreaElement | null>(null);
  const setEmailSubjectRef = (ref: HTMLTextAreaElement) => {
    emailSubjectRef.current = ref;
  };

  const ids = [randomId()];

  const { setContent, emails, setEmails, subject, setSubject, onSave, close } =
    useSoldEmailNotifications({ campaign, menuType, campaignId });

  const onAddEmail = () => {
    setEmails([...emails, { value: '', id: randomId() }]);
    ids.push(randomId());
  };

  const updateSubject = (value: string, isAttribute: boolean) => {
    if (emailSubjectRef.current) {
      const newText = isAttribute
        ? getUpdatedTextWithAttribute(subject, value, emailSubjectRef.current)
        : getUpdatedTextWithEmoji(subject, value, emailSubjectRef.current);

      setSubject(newText);
    }
  };

  const { setRef } = useSlideoutOnClose(close, menuType);

  return (
    <Menu className={css.container} setRef={setRef}>
      <div className={css.header}>
        <BackButton onClick={close} />
        <Heading size={3}>Email Notifications</Heading>

        <span className={css.close} onClick={close}>
          <CloseOutlined />
        </span>
      </div>
      <Menu.Content className={css.content}>
        <div className={css.emailRow}>
          <div className={css.emailField}>
            <EmailRed className={css.emailIcon} />
            <div className={css.subtitle}>Send to:</div>
          </div>

          <div className={css.emailInputContainer}>
            {emails.map((email, idx) => (
              <div className={css.emailInputRow} key={email.id}>
                <EmailInput
                  inputProps={{
                    placeholder: '',
                    style: { minHeight: '32px' },
                  }}
                  className={css.input}
                  strict
                  withIcon={false}
                  onChange={v => {
                    const dup = [...emails];
                    dup[idx] = { value: v.email, id: email.id };
                    setEmails(dup);
                  }}
                  defaultValue={emails[idx].value}
                />
                {!!idx && (
                  <DeleteOutlined
                    className={css.deleteButton}
                    onClick={() => {
                      const dup = [...emails];
                      dup.splice(idx, 1);
                      ids.splice(idx, 1);
                      setEmails(dup);
                    }}
                  />
                )}
              </div>
            ))}

            <Button
              color="link"
              onClick={onAddEmail}
              disabled={emails.length >= 3}
              leftIcon={<PlusCircleOutlined color={colors.accentBlue} />}
            >
              {' '}
              Add Email
            </Button>
          </div>
        </div>

        <div className={css.column}>
          <span className={css.subtitle}>Email subject</span>
          <div className={css.row}>
            <AttributeContainingTextInput
              className={css.input}
              value={subject}
              onChange={e => setSubject(e.target.value)}
              attributes={attributes}
              inputRef={setEmailSubjectRef}
              enabledSuggestions={false}
              multiline={false}
            >
              <div className={css.inputActions}>
                <EmojiPicker
                  className={css.emoji}
                  onEmojiPicked={v => updateSubject(v, false)}
                >
                  <SmileOutlined />
                </EmojiPicker>

                <AttributePicker
                  className={css.picker}
                  onAttributePicked={v => updateSubject(v, true)}
                >
                  <BracketsOutlined className={css.code} />
                </AttributePicker>
              </div>
            </AttributeContainingTextInput>
          </div>
        </div>
        <div className={css.column}>
          <div className={css.subtitle}>Email body</div>

          <Editor
            styleMenubar={{ display: 'none' }}
            attributes={attributes}
            schema={schema}
            value={
              campaign?.soldConfigurations?.unicorn?.notifications?.email
                ?.content
            }
            onChange={state => setContent(state)}
          />
        </div>
        <div className={css.row}>
          <Button css={{ container: css.save }} onClick={onSave}>
            Save
          </Button>
          <Button
            color="transparent"
            onClick={close}
            css={{ container: css.back }}
          >
            Back
          </Button>
        </div>
      </Menu.Content>
    </Menu>
  );
};
